import { useEffect, useRef, useState } from 'react';
import {
  AnimatePresence,
  LayoutGroup,
  motion,
  useAnimate,
  useInView,
  useMotionValue,
  useMotionValueEvent,
  useTransform
} from 'framer-motion';
import { rgba } from 'polished';
import styled from 'styled-components';
import { easing } from 'ts-easing';

import { MagicButton as MagicIcon } from '@/components/code-illustrations/cells/MagicButton';
import * as DS from '@/components/design-system';
import { InfoText } from '@/components/global/InfoText';
import { GRID_SIZE } from '@/components/global/PaperGrid';
import { PromptBarContainer } from '@/components/magic/prompt-bar/SharedStyles';
import { tightSpring } from '@/constants/animation';
import { ReactComponent as IconMagicalAI } from '@/images/icons/icon-product-overview-magical-ai.svg';
import { INTERNAL_LINKS } from '@/utils';
import { mixString } from '@/utils/animation';

import CellPresence from './CellPresence';
import Cells from './Cells';
import LoadingEllipses from './LoadingEllipses';

const Wrapper = styled(motion.div)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 1;
`;

const InfoWrapper = styled.div`
  margin-bottom: 48px;
  padding: 0 ${GRID_SIZE}px;

  .info-text {
    align-items: center;
    max-width: 520px;
    text-align: center;
    text-wrap: balance;
  }
`;

const PromptBar = styled(PromptBarContainer)`
  max-width: 610px;
  box-shadow: ${({ theme }) => rgba(theme.borderColor.MUTED, 0.1)} 0px 0px 10px
    0px;
  border: 1px solid transparent;
  overflow: hidden;

  > * {
    background-color: #1d1d25;
    background-image: linear-gradient(
      ${({ theme }) => rgba(theme.appUI.magic.primaryColor, 0.05)} 0%,
      rgba(253, 170, 170, 0.01) 100%
    );
  }
`;

const PrompBarWrapper = styled(motion.div)`
  position: relative;
  display: grid;
  place-items: center;
  justify-self: stretch;
  padding: 0 28px;
  z-index: 2;
`;

const PromptInner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.appUI.magic.primaryColor};
  font-family: ${({ theme }) => theme.fontFamily.SANS};
  font-size: 16px;
`;

const PromptIcon = styled(MagicIcon)`
  width: 28px;
  height: 28px;
`;

const Prompt = styled.span`
  display: grid;
  justify-items: start;

  > * {
    grid-area: 1 / 1;
  }
`;

const PromptText = styled(motion.span)`
  --cursorWidth: 2px;
  --duration: 0.8s;

  position: relative;
  padding-right: 33px;
  min-height: ${({ theme }) => theme.lineHeight.DEFAULT};
  min-width: var(--cursorWidth);

  &::after {
    content: '';
    display: inline-block;
    vertical-align: bottom;
    height: ${({ theme }) => theme.lineHeight.DEFAULT};
    width: var(--cursorWidth);
    margin-left: 4px;
    border-radius: 999px;
    background-color: ${({ theme }) => theme.appUI.magic.primaryColor};
    animation: cursorBlink var(--duration) steps(2) infinite;
    animation-delay: calc(var(--duration) * -0.75s);
  }

  @keyframes cursorBlink {
    from {
      opacity: 0;
    }
  }
`;

const PromptBarToast = styled(motion.div)`
  height: 20px;
  line-height: 20px;
  border-radius: 2px 2px 0 0;
  border: 1px dashed ${({ theme }) => theme.borderColor.DEFAULT};
  border-width: 1px 1px 0 1px;
  opacity: 0;
  backdrop-filter: blur(10px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  max-width: 75%;
  padding: 0 6px;
  background-color: ${({ theme }) =>
    rgba(theme.appUI.magic.tertiaryColor, 0.15)};
  color: ${({ theme }) => theme.appUI.magic.tertiaryColor};
`;

const PromptBarOffset = styled(motion.div)`
  position: relative;
  display: grid;
  align-items: start;
  justify-items: center;
  justify-self: stretch;

  > * {
    grid-area: 1 / 1;
  }
`;

const Illustration = styled.div`
  position: relative;
  align-self: stretch;
  display: grid;
  height: 450px;
  margin-bottom: 48px;
  z-index: -2;
  border-radius: 3px;
  background-image: linear-gradient(
    90deg,
    transparent,
    rgba(255, 192, 202, 0.0075) 10vw,
    rgba(255, 192, 202, 0.0075) calc(100% - 10vw),
    transparent
  );

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    --gradient-ramp: 20vw;
    height: 280px;
  }

  &::after {
    --gradient-ramp: 30vw;

    content: '';
    mask-image: url('/images/magic/dot-grid-bkg.svg');
    position: absolute;
    inset: 0;
    z-index: -1;
    background-image: linear-gradient(
      90deg,
      transparent,
      rgba(253, 170, 170, 0.5) var(--gradient-ramp),
      rgba(253, 170, 170, 0.5) calc(100% - var(--gradient-ramp)),
      transparent
    );
  }
`;

const IllustrationContent = styled(motion.div)`
  position: absolute;
  inset: 0;
  display: grid;
  grid-column: span 1;
  place-items: center;

  > * {
    grid-area: 1 / 1;
  }
`;

const CellContainer = styled(motion.div)`
  display: grid;
  place-items: center;
  justify-self: stretch;
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-image: linear-gradient(
      to right,
      ${({ theme }) => rgba(theme.borderColor.DEFAULT, 0)},
      ${({ theme }) => rgba(theme.borderColor.DEFAULT, 0.7)},
      ${({ theme }) => rgba(theme.borderColor.DEFAULT, 0)}
    )
    30 / 1 / 0 stretch;
  border-width: 1px 0px;
  border-style: solid;
`;

const PROMPT_TEXT = 'Roll up average retention over time for all segments';
const getPromptSubstring = mixString(PROMPT_TEXT);

export default function MagicTout() {
  const triggerRef = useRef<HTMLDivElement>(null);
  const promptTextRef = useRef<HTMLSpanElement>(null);

  const isInView = useInView(triggerRef, {
    margin: '-10% 0% -10% 0%',
    amount: 'all',
    once: true
  });
  const [scope, animate] = useAnimate();
  const promptProgress = useMotionValue(0);
  const promptPress = useMotionValue(0);
  const promptScale = useTransform(promptPress, [0, 1], [1, 0.96]);
  const promptBarToastProgress = useMotionValue(0);
  const promptBarToastY = useTransform(
    promptBarToastProgress,
    [0, 1],
    ['0%', '-100%']
  );
  const promptBarOffset = useMotionValue('0%');

  const state = useMotionValue(0);
  const [illustrationState, setIllustrationState] = useState(0);
  const lastState = useRef(state.get());

  useMotionValueEvent(state, 'change', s => {
    const curr = Math.floor(s);
    if (curr === lastState.current) return;

    lastState.current = curr;
    setIllustrationState(curr);
  });

  useMotionValueEvent(promptProgress, 'change', p => {
    const promptEl = promptTextRef.current;
    if (!promptEl) return;

    const oldPromptNode = promptEl.firstChild;
    if (oldPromptNode) promptEl.removeChild(oldPromptNode);

    const prompt = getPromptSubstring(p);
    const promptNode = document.createTextNode(prompt);
    promptEl.appendChild(promptNode);
  });

  useEffect(() => {
    if (!isInView) return;

    // STATE:
    // 0. Placeholder Prompt
    // 1. Typing Prompt
    // 2. Thinking Cell
    // 3. Generating SQL
    // 4. Querying
    // 5. Display Table
    // 6. Display Python Cell
    // 7. Write Python
    // 8. Generating Visuals
    // 9. Display Graph
    // 10. End State
    animate([
      // 0. Placeholder Prompt
      [state, 0, { duration: 0.001 }],
      [promptBarOffset, '0%', { duration: 0.001, at: '<' }],
      [
        promptPress,
        [0, 1, 0],
        { ease: easing.outCirc, duration: 0.3, delay: 0.01 }
      ],

      // 1. Typing Prompt
      [state, [0, 1], { duration: 0.001, at: '-0.3' }],
      [
        promptProgress,
        [0, 1],
        {
          ease: 'linear',
          duration: 1,
          delay: 0.6
        }
      ],
      [
        promptPress,
        [0, 1, 0],
        { ease: easing.outCirc, duration: 0.3, delay: 0.3 }
      ],
      [
        promptBarToastProgress,
        [0, 1],
        { type: 'spring', damping: 32, stiffness: 571, at: '-0.15' }
      ],

      // 2. Thinking Cell
      // 3. Generating Sql
      [state, [2, 3], { duration: 0.001, at: '<' }],
      [
        promptBarOffset,
        ['0%', `30%`],
        { type: 'spring', damping: 32, stiffness: 571, mass: 1, at: '<' }
      ],

      // 4. Writing Sql
      [state, [3, 4], { ease: 'linear', duration: 1 }],

      // 5. Display Table
      [state, [4, 5], { duration: 0.001, delay: 1 }],

      // 6. Display Python
      [state, [5, 6], { duration: 0.001, delay: 0.7 }],

      // 7. Write Python
      [state, [6, 7], { ease: 'linear', duration: 0.7, delay: 0.5 }],

      // 8. Generating Visuals
      [state, [7, 8], { duration: 0.001 }],

      // 9. Display Graph
      [state, [8, 9], { duration: 0.001, delay: 0.8 }],

      // 10. End State
      [state, [9, 10], { duration: 0.001, delay: 1 }],
      [
        promptBarToastProgress,
        [1, 0],
        { type: 'spring', damping: 32, stiffness: 571, delay: 0.1 }
      ]
    ]);
  }, [
    animate,
    isInView,
    promptBarOffset,
    promptBarToastProgress,
    promptPress,
    promptProgress,
    state
  ]);

  return (
    <LayoutGroup>
      <Wrapper ref={scope}>
        <InfoWrapper>
          <InfoText
            description="Generate queries, write code, create visualizations, fix bugs, and
          kickstart whole analyses - all from a prompt."
            icon={<IconMagicalAI />}
            title="Magical AI, built right in"
          />
        </InfoWrapper>
        <Illustration
          ref={triggerRef}
          aria-hidden="true"
          aria-label='Animated illustration of magic prompt bar with user typing in the input "Roll up average retention over time for all segments", then magic ai generating corresponding hex cells for sql query, python query and data visualizations.'>
          <IllustrationContent>
            <CellContainer>
              <AnimatePresence>
                {illustrationState > 1 ? (
                  <CellPresence
                    key="cell"
                    style={{ width: '95%', maxWidth: 1100 }}>
                    <Cells
                      illustrationState={illustrationState}
                      state={state}
                    />
                  </CellPresence>
                ) : null}
              </AnimatePresence>
            </CellContainer>
            <PrompBarWrapper
              layout
              style={{
                scale: promptScale,
                placeSelf:
                  illustrationState > 1 ? 'end stretch' : 'center stretch'
              }}
              transition={tightSpring}>
              <PromptBarOffset
                style={{
                  y: promptBarOffset
                }}>
                <PromptBarToast
                  style={{
                    opacity: promptBarToastProgress,
                    y: promptBarToastY
                  }}>
                  {PROMPT_TEXT}
                </PromptBarToast>
                <PromptBar
                  style={{
                    alignSelf: illustrationState > 1 ? 'end' : 'center'
                  }}>
                  <PromptInner>
                    <PromptIcon
                      state={
                        1 < illustrationState && illustrationState < 10
                          ? 'loading'
                          : 'active'
                      }
                    />
                    <Prompt>
                      {(function (s: number) {
                        switch (s) {
                          case 0:
                            return (
                              <span style={{ opacity: 0.4 }}>
                                What would you like to know?
                              </span>
                            );

                          case 1:
                            return <PromptText ref={promptTextRef} />;

                          case 2:
                            return (
                              <span style={{ opacity: 0.4 }}>
                                Scheming
                                <LoadingEllipses />
                              </span>
                            );

                          case 3:
                            return (
                              <span style={{ opacity: 0.4 }}>
                                Generating SQL
                                <LoadingEllipses />
                              </span>
                            );

                          case 4:
                          case 5:
                            return (
                              <span style={{ opacity: 0.4 }}>
                                Querying Database
                                <LoadingEllipses />
                              </span>
                            );

                          case 6:
                          case 7:
                            return (
                              <span style={{ opacity: 0.4 }}>
                                Agumenting Data With Python
                                <LoadingEllipses />
                              </span>
                            );

                          case 8:
                          case 9:
                            return (
                              <span style={{ opacity: 0.4 }}>
                                Creating Chart
                                <LoadingEllipses />
                              </span>
                            );

                          case 10:
                          default:
                            return (
                              <span style={{ opacity: 0.4 }}>
                                What would you like to add?
                              </span>
                            );
                        }
                      })(illustrationState)}
                    </Prompt>
                  </PromptInner>
                </PromptBar>
              </PromptBarOffset>
            </PrompBarWrapper>
          </IllustrationContent>
        </Illustration>
        <DS.Button link={INTERNAL_LINKS.Magic} text="Explore AI tools" />
      </Wrapper>
    </LayoutGroup>
  );
}
